import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { format, isSameDay, isToday, isTomorrow } from 'date-fns';

import { OutlookDataService } from '../../services/outlook-data.service';
import { IOutlookAgendaDay, IOutlookEvent } from '../../models/IOutlookEvent.interface';
import { OutlookCalendarConsent } from '../../models/IOutlookPanel.interface';

/**
 * The next week's calendar, and the consent handshake that guards it.
 *
 * Calendar rides on a scope the base sign-in does not request, so this is the one tab that can be
 * unavailable while everything else works. The gate is deliberate rather than an error: the probe
 * is silent, the prompt only ever follows a click on the button that says it will prompt, and the
 * "open Outlook Calendar" link is present in every state so the user reaches their calendar even
 * when consent is refused outright.
 */
@Component({
  selector: 'app-outlook-calendar-tab',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './outlook-calendar-tab.component.html',
  styleUrls: ['./outlook-calendar-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlookCalendarTabComponent implements OnInit {
  private readonly data = inject(OutlookDataService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  consent: OutlookCalendarConsent = 'unknown';
  days: IOutlookAgendaDay[] = [];
  loading = false;
  requesting = false;

  readonly outlookCalendarUrl = 'https://outlook.office.com/calendar/';

  ngOnInit(): void {
    this.data.calendarConsent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((consent) => {
      this.consent = consent;
      this.cdr.markForCheck();

      if (consent === 'granted' && this.days.length === 0) {
        this.loadAgenda();
      }
    });

    // Silent probe. Deliberately cannot prompt — see hasCalendarConsent in the Graph service.
    this.data.checkCalendarConsent();
  }

  /** Interactive consent. Only reachable from a click, which is what keeps the popup unblocked. */
  requestConsent(): void {
    this.requesting = true;
    this.cdr.markForCheck();

    this.data
      .requestCalendarConsent()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.requesting = false;
        this.cdr.markForCheck();
      });
  }

  refresh(): void {
    this.loadAgenda(true);
  }

  openInOutlook(event?: IOutlookEvent): void {
    window.open(event?.webLink || this.outlookCalendarUrl, '_blank', 'noopener');
  }

  join(event: IOutlookEvent, mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();
    if (event.joinUrl) {
      window.open(event.joinUrl, '_blank', 'noopener');
    }
  }

  trackByDay(_index: number, day: IOutlookAgendaDay): string {
    return day.label;
  }

  trackByEvent(_index: number, event: IOutlookEvent): string {
    return event.id;
  }

  private loadAgenda(force = false): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.data
      .getAgenda(7, force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((events) => {
        this.days = this.groupByDay(events);
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  /** Buckets occurrences into calendar days, which is the only shape an agenda reads well in. */
  private groupByDay(events: IOutlookEvent[]): IOutlookAgendaDay[] {
    const days: IOutlookAgendaDay[] = [];

    events
      .filter((e) => !e.isCancelled && e.start)
      .forEach((event) => {
        const start = new Date(event.start);
        if (isNaN(start.getTime())) {
          return;
        }

        const bucket = days.find((d) => isSameDay(d.date, start));
        if (bucket) {
          bucket.events.push(event);
        } else {
          days.push({ date: start, label: this.labelFor(start), events: [event] });
        }
      });

    return days;
  }

  private labelFor(date: Date): string {
    if (isToday(date)) {
      return `Today · ${format(date, 'd MMM')}`;
    }
    if (isTomorrow(date)) {
      return `Tomorrow · ${format(date, 'd MMM')}`;
    }
    return format(date, 'EEEE · d MMM');
  }
}
